// src/pages/document/DocumentTypeDetailPage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useDocumentDocumentType, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Badge,
  Alert,
  ErrorAlert,
} from '@react/components';

/**
 * Document type detail page.
 */
export const DocumentTypeDetailPage = observer(() => {
  const { typeId } = useParams<{ typeId: string }>();
  const navigate = useNavigate();
  const store = useDocumentDocumentType();
  const { t } = useI18n();
  const item = store.currentItem;

  useEffect(() => {
    if (typeId) {
      store.GetDocumentType(typeId);
    }
    return () => {
      store.clearCurrent();
    };
  }, [typeId]);

  const handleDelete = async () => {
    if (!typeId || !confirm(t('documentType.deleteConfirm'))) return;
    try {
      await store.DeleteDocumentType(typeId);
      navigate('/documents/types');
    } catch {
      // Error is handled by store
    }
  };

  if (store.isLoadingItem) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (store.error) {
    return (
      <>
        <ErrorAlert error={store.error} />
        <Link to="/documents/types" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </>
    );
  }
  if (!item) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/documents/types" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="sr-only">{t('documentType.details')}</h1>
        <nav className="text-sm">
          <Link to="/documents" className="text-muted-foreground hover:text-foreground">
            {t('document.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/documents/types" className="text-muted-foreground hover:text-foreground">
            {t('documentType.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('documentType.details')}</span>
        </nav>
        <div className="flex gap-2 sm:ml-auto">
          <Link to={`/documents/types/${typeId}/edit`}>
            <Button variant="secondary">{t('common.edit')}</Button>
          </Link>
          <Button variant="danger" onClick={handleDelete}>
            {t('common.delete')}
          </Button>
        </div>
      </div>

      {/* Identity */}
      <Card>
        <CardHeader>
          <CardTitle>{t('documentType.identity')}</CardTitle>
          <CardDescription>{t('documentType.identityDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Slug</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.slug ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Display Name</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.display_name ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Description</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.description ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Product ID</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.product_id ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Source Entity Type</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.source_entity_type ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Icon</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.icon ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Color</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.color ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Active</dt>
              <dd className="text-sm text-foreground col-span-2">
                <Badge variant={(item as any)?.is_active ? 'success' : 'secondary'}>
                  {(item as any)?.is_active ? t('common.yes') : t('common.no')}
                </Badge>
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Lifecycle Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>{t('documentType.lifecycle')}</CardTitle>
          <CardDescription>{t('documentType.lifecycleDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Requires Approval</dt>
              <dd className="text-sm text-foreground col-span-2">
                <Badge variant={(item as any)?.requires_approval ? 'success' : 'secondary'}>
                  {(item as any)?.requires_approval ? t('common.yes') : t('common.no')}
                </Badge>
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Requires Acknowledgment</dt>
              <dd className="text-sm text-foreground col-span-2">
                <Badge variant={(item as any)?.requires_acknowledgment ? 'success' : 'secondary'}>
                  {(item as any)?.requires_acknowledgment ? t('common.yes') : t('common.no')}
                </Badge>
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Enable Versioning</dt>
              <dd className="text-sm text-foreground col-span-2">
                <Badge variant={(item as any)?.enable_versioning ? 'success' : 'secondary'}>
                  {(item as any)?.enable_versioning ? t('common.yes') : t('common.no')}
                </Badge>
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Enable Review Cycle</dt>
              <dd className="text-sm text-foreground col-span-2">
                <Badge variant={(item as any)?.enable_review_cycle ? 'success' : 'secondary'}>
                  {(item as any)?.enable_review_cycle ? t('common.yes') : t('common.no')}
                </Badge>
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Enable Audit Log</dt>
              <dd className="text-sm text-foreground col-span-2">
                <Badge variant={(item as any)?.enable_audit_log ? 'success' : 'secondary'}>
                  {(item as any)?.enable_audit_log ? t('common.yes') : t('common.no')}
                </Badge>
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Defaults */}
      <Card>
        <CardHeader>
          <CardTitle>{t('documentType.defaults')}</CardTitle>
          <CardDescription>{t('documentType.defaultsDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Default Workflow</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.default_workflow ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Default Category</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.default_category ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Default Numbering Scheme ID</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.default_numbering_scheme_id ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Default Review Period</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.default_review_period ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Default Confidentiality</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.default_confidentiality ?? '')}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Kafka Topics */}
      <Card>
        <CardHeader>
          <CardTitle>{t('documentType.kafkaTopics')}</CardTitle>
          <CardDescription>{t('documentType.kafkaTopicsDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Source Created Topic</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.source_created_topic ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Source Updated Topic</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.source_updated_topic ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Source Deleted Topic</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.source_deleted_topic ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Source Status Changed Topic</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.source_status_changed_topic ?? '')}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Timestamps */}
      <Card>
        <CardHeader>
          <CardTitle>{t('documentType.timestamps')}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created At</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.created_at ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Updated At</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.updated_at ?? '')}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
});

export default DocumentTypeDetailPage;
